import React, { useContext } from "react"
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch"
import {
    Avatar,
    Box,
    Button,
    componentStyles,
    Divider,
    Highlight,
    HorizontalStack,
    ImpersonationBannerContent,
    Modal,
    Spacer,
    Spinner,
    Tag,
    Text,
    TextExtraSmall,
    TextInput,
    TextSecondaryExtraSmall,
    TextSmallSecondary,
    useColorModeValue,
} from "@mallardbay/lib-react-components"

import type { UserFieldsFragment } from "~graphql/users.generated"
import { UserContext } from "~contexts/user-provider"
import { usePersistedPortalEntities } from "~utils/hooks/use-persisted-portal-entities"
import COPY from "~config/copy-constants"
import { navigateWithHardReload } from "~utils/helpers/navigation-helpers"
import useStopImpersonation from "~utils/hooks/use-stop-impersonation"
import { useAuth } from "~utils/hooks/use-auth"
import { getFullName } from "~utils/helpers/string-helpers"
import useSearchOutfittersForImpersonation from "~utils/hooks/use-search-outfitters-for-impersonation"
import OutfitterImpersonationItem from "~components/shared/auth/outfitter-impersonation-item"

interface Props {
    readonly isOpen: boolean
    readonly onClose: () => void
}

const IMPERSONATION_BANNER_COPY = {
    loggedInAs: COPY.LOGGED_IN_AS,
    nowViewing: COPY.NOW_VIEWING,
}

export default function ImpersonationModal({ isOpen, onClose }: Props) {
    const { user: currentUser, isImpersonating } = useContext(UserContext)
    const { authUser } = useAuth()
    const styles = useStyles()
    const { query, setQuery, results, total, isLoading } =
        useSearchOutfittersForImpersonation({
            isEnabled: isOpen,
            excludedUserId: currentUser?.id,
        })

    const handleClose = () => {
        setQuery("")
        onClose()
    }

    return (
        <Modal
            title={COPY.SWITCH_IMPERSONATION}
            isOpen={isOpen}
            onClose={handleClose}
            shouldAddPadding
        >
            <ImpersonationBannerContent
                loggedInUserName={authUser?.name ?? ""}
                loggedInUserEmail={authUser?.email ?? ""}
                viewingAsName={getFullName(currentUser)}
                viewingAsEmail={currentUser?.email ?? ""}
                copy={IMPERSONATION_BANNER_COPY}
                style={styles.banner}
            />
            <Divider hasMarginBottom hasMarginTop />
            <TextSmallSecondary>
                {COPY.SEARCH_FOR_AN_OUTFITTER_TO_VIEW_AS}
            </TextSmallSecondary>
            <HorizontalStack style={styles.searchRow}>
                <TextInput
                    startIcon={faSearch}
                    value={query}
                    onChange={setQuery}
                    placeholder={COPY.SEARCH_OUTFITTERS}
                />
                <TextSecondaryExtraSmall>{total}</TextSecondaryExtraSmall>
            </HorizontalStack>
            <Divider hasMarginBottom hasMarginTop />

            {isImpersonating && currentUser && (
                <ViewingImpersonationItem query={query} user={currentUser} />
            )}
            {isLoading && (
                <Box style={styles.centerContainer}>
                    <Spinner size="sm" />
                </Box>
            )}
            {!isLoading && results.length === 0 && (
                <Box style={styles.emptyState}>{COPY.NO_OUTFITTERS_FOUND}</Box>
            )}
            {results.map((result) => (
                <OutfitterImpersonationItem
                    key={result.outfitter.id}
                    result={result}
                    query={query}
                />
            ))}
        </Modal>
    )
}

interface ViewingImpersonationItemProps {
    readonly user: UserFieldsFragment
    readonly query: string
}

// eslint-disable-next-line react/no-multi-comp -- TODO: Extract component to separate file
function ViewingImpersonationItem({
    user,
    query,
}: ViewingImpersonationItemProps) {
    const { clearAllPersistedPortalEntities } = usePersistedPortalEntities()
    const { stopImpersonation, isLoading } = useStopImpersonation({
        onSuccess: () => {
            clearAllPersistedPortalEntities()
            navigateWithHardReload()
        },
    })
    const styles = useViewingItemStyles()

    return (
        <HorizontalStack style={styles.viewingRow}>
            <Avatar
                style={styles.avatar}
                src={user.avatar_url ?? ""}
                name={`${user.first_name} ${user.last_name}`}
            />
            <Box>
                <HorizontalStack>
                    <Text numberOfLines={1} style={styles.highlightName}>
                        <Highlight query={query} style={styles.highlight}>
                            {`${user.first_name} ${user.last_name}`}
                        </Highlight>
                    </Text>
                    <Tag colorScheme="brand" style={styles.viewingTag}>
                        {COPY.VIEWING}
                    </Tag>
                </HorizontalStack>
                <TextExtraSmall numberOfLines={1}>
                    <Highlight query={query} style={styles.highlight}>
                        {user.email}
                    </Highlight>
                </TextExtraSmall>
            </Box>
            <Spacer />
            <Button
                size="sm"
                colorScheme="red"
                isLoading={isLoading}
                onClick={() => {
                    void stopImpersonation()
                }}
            >
                {COPY.EXIT_IMPERSONATION}
            </Button>
        </HorizontalStack>
    )
}

function useStyles() {
    return componentStyles({
        banner: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        searchRow: {
            alignItems: "center",
        },
        centerContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "120px",
        },
        emptyState: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "120px",
            fontWeight: "bold",
        },
    })
}

function useViewingItemStyles() {
    const activeBgColor = useColorModeValue("brand.50", "brand.800")

    return componentStyles({
        viewingRow: {
            py: 2,
            px: 2,
            gap: 3,
            rounded: "xl",
            bgColor: activeBgColor,
        },
        avatar: {
            shadow: "lg",
        },
        highlightName: {
            fontSize: "15px",
            fontWeight: "bold",
        },
        highlight: {
            bg: "yellow.200",
        },
        viewingTag: {
            fontWeight: "bold",
            fontSize: "sm",
        },
    })
}
